/**
 * Client-side Input Quality Assessment
 * 
 * Mirrors the backend's assessInputQuality() logic to provide
 * real-time guidance in the intake textarea before submission.
 */

export type InputQualityLevel = 'insufficient' | 'thin' | 'adequate' | 'strong';

export interface InputQualityResult {
  level: InputQualityLevel;
  wordCount: number;
  hasDecisionSignal: boolean;
  hasMultipleOptions: boolean;
  guidance: string | null;
}

// Decision-related signal patterns (German)
const DECISION_SIGNALS = /\b(oder|zwischen|entscheid|option|alternativ|variante|richtung|wahl|dilemma|abwäg|entweder|versus|vs\.?|gegenüber|stattdessen|anstatt)\b/i;

// Patterns suggesting two directions
const MULTI_OPTION_SIGNALS = /\b(einerseits|andererseits|auf der einen|auf der anderen|option [ab]|variante [ab12]|entweder.*oder|versus|vs\.?)\b/i;

export function assessInputQuality(text: string): InputQualityResult {
  const trimmed = text.trim();
  const words = trimmed.split(/\s+/).filter(w => w.length > 0);
  const wordCount = words.length;
  const hasDecisionSignal = DECISION_SIGNALS.test(trimmed);
  const hasMultipleOptions = MULTI_OPTION_SIGNALS.test(trimmed);

  // Too short to assess
  if (wordCount < 15) {
    return { level: 'insufficient', wordCount, hasDecisionSignal, hasMultipleOptions, guidance: null };
  }

  // 15-29 words: show early guidance
  if (wordCount < 30) {
    if (!hasDecisionSignal) {
      return {
        level: 'insufficient', wordCount, hasDecisionSignal, hasMultipleOptions,
        guidance: 'Beschreibe die Alternativen, zwischen denen du schwankst.',
      };
    }
    return {
      level: 'thin', wordCount, hasDecisionSignal, hasMultipleOptions,
      guidance: 'Mehr Kontext verbessert die Analyse deutlich.',
    };
  }

  // 30-49 words
  if (wordCount < 50) {
    if (!hasDecisionSignal) {
      return {
        level: 'thin', wordCount, hasDecisionSignal, hasMultipleOptions,
        guidance: 'Benenne die Optionen, zwischen denen du entscheidest.',
      };
    }
    return {
      level: 'adequate', wordCount, hasDecisionSignal, hasMultipleOptions,
      guidance: 'Ausreichend. Mehr Kontext verbessert die Analyse.',
    };
  }

  // 50+ words
  if (!hasDecisionSignal) {
    return {
      level: 'adequate', wordCount, hasDecisionSignal, hasMultipleOptions,
      guidance: 'Tipp: Benenne die Optionen, zwischen denen du entscheidest.',
    };
  }

  return {
    level: 'strong', wordCount, hasDecisionSignal, hasMultipleOptions,
    guidance: 'Ausreichend für Strukturierung',
  };
}
